import crc32 from 'fbjs/lib/crc32';

export type Variant = string;
export type Weight = number;

export interface Experiment {
  name: string;
  variants: Variant[];
  weights: Weight[];
}

// Sorted array of the variant names, example: ["A", "B", "C"]
export const getSortedVariants = (experiment: Experiment): Variant[] =>
  [...experiment.variants].sort();

// Array of the variant weights, also sorted by variant name. For example, if
// variant C had weight 2, variant A had weight 4, and variant B had weight 8
// return [4, 8, 2] to correspond with ["A", "B", "C"]// Sorted array of the variant names, example: ["A", "B", "C"]
export const getSortedVariantWeights = (experiment: Experiment): Weight[] => {
  const variants = experiment.variants;
  const sortedVariants = getSortedVariants(experiment);
  const weights = experiment.weights;

  return sortedVariants.map(
    variantName => weights[variants.indexOf(variantName)],
  );
};

// A random number between 0 and weightSum
export const initialWeightedIndex = (
  name: string,
  weightSum: number,
  userIdentifier?: string,
): number =>
  typeof userIdentifier === 'string'
    ? Math.abs(crc32(`${userIdentifier}/${name}`) % weightSum)
    : Math.floor(Math.random() * weightSum);

/*
  Choosing a weighted variant:
  For C, A, B with weights 2, 4, 8

  variants = A, B, C
  weights = 4, 8, 2
  weightSum = 14
  weightedIndex = 9

  AAAABBBBBBBBCC
  ========^
  Select B
*/
export default function getSelectedVariant(
  experiment: Experiment,
  userIdentifier?: string,
): Variant {
  const variants = getSortedVariants(experiment);
  const weights = getSortedVariantWeights(experiment);

  const weightSum = weights.reduce((a, b) => a + b, 0);
  let weightedIndex = initialWeightedIndex(
    experiment.name,
    weightSum,
    userIdentifier,
  );

  // Iterate through the sorted weights, and deduct each from the weightedIndex.
  // If weightedIndex drops < 0, select the variant. If weightedIndex does not
  // drop < 0, default to the last variant in the array that is initially assigned.
  let selectedVariant = variants[variants.length - 1];
  for (let index = 0; index < weights.length; index++) {
    weightedIndex -= weights[index];
    if (weightedIndex < 0) {
      selectedVariant = variants[index];
      break;
    }
  }

  return selectedVariant;
}
